import React, { useEffect, useRef, useState } from "react"

interface Option {
  value: string
  label: string
}

interface MultiSelectDropdownProps {
  title: string
  options: Option[]
  selectedValues: string[]
  onChange: (selectedValues: string[]) => void
}

const MultiSelectFilter: React.FC<MultiSelectDropdownProps> = ({
  title,
  options,
  selectedValues,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev)
  }

  const handleCheckboxChange = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value) // Uncheck
      : [...selectedValues, value] // Check

    onChange(newSelectedValues)
  }

  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div ref={rootRef} className="relative">
      <button
        style={{ width: "10rem" }}
        onClick={toggleDropdown}
        className="flex items-center justify-between w-full px-3 py-[2px] text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <span className="text-xs truncate max-w-[7.5rem]">{title}</span>
        <svg
          className={`w-5 h-5 ml-2 transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-56 overflow-y-auto">
          <div className="py-1 overflow-y-auto custom-scrollbar">
            {options?.map((option) => (
              <label
                key={option.value}
                className="flex items-center px-4 py-2 space-x-2 hover:bg-gray-100"
                onMouseDown={(e) => e.preventDefault()}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                  className="w-3 h-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 sticky left-4"
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <span className="text-xs text-gray-700 whitespace-nowrap">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiSelectFilter
